import { Settings, Shield, TrendingUp, Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import ConsultationModal from '@/components/ConsultationModal';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

const ITConsulting = () => {
  const { t } = useLanguage();
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);

  const features = [
    {
      icon: TrendingUp,
      title: t('itConsulting.technologyStrategy'),
      description: t('itConsulting.technologyStrategyDescription')
    },
    {
      icon: Settings,
      title: t('itConsulting.infrastructurePlanning'), 
      description: t('itConsulting.infrastructurePlanningDescription')
    },
    {
      icon: Shield,
      title: t('itConsulting.securityConsulting'),
      description: t('itConsulting.securityConsultingDescription')
    },
    {
      icon: Users,
      title: t('itConsulting.digitalTransformation'),
      description: t('itConsulting.digitalTransformationDescription')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Back Button */}
      <section className="pt-24 pb-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <Link to="/">
            <Button variant="outline-light" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('jobs.backToHome')}
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Hero Section */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center animate-fade-in">
            <div className="inline-flex p-6 rounded-3xl bg-gradient-to-br from-secondary to-accent mb-8 glow-accent">
              <Settings className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold mb-6">
              {t('itConsulting.title')}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
              {t('itConsulting.heroDescription')}
            </p>
            <Button 
              size="lg" 
              className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
              onClick={() => setIsConsultationOpen(true)}
            >
              {t('itConsulting.strategicGuidance')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('itConsulting.ourExpertise')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="group p-6 sm:p-8 rounded-3xl card-gradient border border-border/50 hover:border-primary/50 transition-smooth hover:scale-105 hover:glow-primary animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-secondary to-accent glow-accent mb-6">
                    <feature.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-display font-bold mb-4 text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting Areas */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('itConsulting.consultingAreas')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { 
                title: t('itConsulting.technologyAssessment'), 
                items: [t('itConsulting.currentStateAnalysis'), t('itConsulting.gapIdentification'), t('itConsulting.technologyAudit'), t('itConsulting.performanceReview')] 
              },
              { 
                title: t('itConsulting.strategicPlanning'), 
                items: [t('itConsulting.itRoadmapCreation'), t('itConsulting.budgetPlanning'), t('itConsulting.riskManagement'), t('itConsulting.roiAnalysis')] 
              },
              { 
                title: t('itConsulting.implementationSupport'), 
                items: [t('itConsulting.projectManagement'), t('itConsulting.changeManagement'), t('itConsulting.trainingPrograms'), t('itConsulting.qualityAssurance')] 
              },
              { 
                title: t('itConsulting.aiConsulting'), 
                items: [t('itConsulting.aiStrategyDevelopment'), t('itConsulting.aiToolSelection'), t('itConsulting.aiWorkflowOptimization'), t('itConsulting.aiPerformanceMonitoring')] 
              }
            ].map((area, index) => (
              <div 
                key={area.title}
                className="p-6 sm:p-8 rounded-3xl card-gradient border border-border/50 animate-fade-in"
                style={{ animationDelay: `${index * 0.3}s` }}
              >
                <h3 className="text-2xl font-display font-bold mb-6 gradient-text">
                  {area.title}
                </h3>
                <ul className="space-y-3">
                  {area.items.map((item) => (
                    <li key={item} className="flex items-center text-foreground">
                      <div className="w-2 h-2 bg-primary rounded-full mr-3 glow-primary"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-display font-bold mb-6">
            {t('itConsulting.transformTechnologyStrategy')}
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            {t('itConsulting.transformTechnologyStrategyDescription')}
          </p>
          <Button 
            size="lg" 
            className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
            onClick={() => setIsConsultationOpen(true)}
          >
            {t('itConsulting.scheduleStrategySession')}
          </Button>
        </div>
      </section>

      <Footer />
      
      <ConsultationModal 
        open={isConsultationOpen} 
        onOpenChange={setIsConsultationOpen} 
      />
    </div>
  );
};

export default ITConsulting;